import { getUrlWithVariant, ProductForm } from '@shopify/theme-product-form';
import productGalleryCarousel from './product-gallery-carousel';
import productGalleryZoomModal from './product-gallery-zoom-modal';
import postArtwork from './post-artwork';

/**
 *  Product Configurables
 */
class ProductConfigurables {
  /**
   * Constructor
   * @param {Object}  props
   * @param {Element} productForm
   * @param {Element} productSection
   */
  constructor({ productForm, productSection }) {
    // Config
    this.c = {
      active: 'is-active',
      customFrameDropdown: '.js-custom-frame-dropdown',
      finalDisplayPrice: '.js-final-display-price',
      hiddenId: '.js-hidden-id',
      colorInput: '.js-color-input',
      input: '.js-input',
      sizeInput: '.js-size-input',
      notify: '.js-product-notify',
      notifyForm: '.js-notify-form',
      option: '.js-option',
      optionGroup: '.js-option-group',
      optionType: '.js-option-type',
      price: '.js-price-original',
      priceCompare: '.js-price-compare',
      btnPrice: '.js-btn-price-original',
      selectedColorOption: '.js-selected-color-option',
      selectedSizeOption: '.js-selected-size-option',
      startingAtPrice: '.js-starting-at-price',
      submitButton: '.js-add-to-bag',
      submitButtonText: '.js-add-to-bag-text',
      submitLink: '.js-product-cta-link',
      hide: 'hide',
      gallery: '.js-product-gallery',
      galleryCarousel: '.js-product-gallery-carousel',
      galleryParent: 'gallery',
      galleryWrap: '.js-product-gallery-wrap',
      detail: '.js-product-detail',
      accBtn: '.js-acc-btn',
      variantColorAttributes: '.js-variant-color-attributes',
      variantSizeAttributes: '.js-variant-size-attributes',
      variantDetails: '.js-variant-details',
      variantPrice: '.js-variant-price',
      fsHiddenInput: '#frameSpecNumber',
      deliveryEstimate: '.js-delivery-estimate',
      deliveryEstimateOverwriteContainer: '.js-delivery-estimate-overwrite-container',
      overwriteMessage: '#deliveryEstimateOverwrite',
    };

    // Elements
    this.$productForm = productForm;
    this.$productSection = productSection;
    this.$hiddenId = this.$productForm?.querySelector(this.c.hiddenId);
    this.$fsHiddenInput = this.$productForm?.querySelector(this.c.fsHiddenInput);
    this.$prices = this.$productSection?.querySelectorAll(this.c.price);
    this.$priceCompare = this.$productSection?.querySelector(this.c.priceCompare);
    this.$productColorOptions = this.$productSection?.querySelector(this.c.colorInput);
    this.$productSizeOptions = this.$productSection?.querySelectorAll(this.c.sizeInput);
    this.$submitButton = this.$productForm?.querySelector(this.c.submitButton);
    this.$submitButtonText = this.$productForm?.querySelector(this.c.submitButtonText);
    this.$submitLink = this.$productForm?.querySelector(this.c.submitLink);
    this.$notify = this.$productSection?.querySelector(this.c.notify);
    this.$notifyForm = this.$notify?.querySelector(this.c.notifyForm);
    this.$galleries = this.$productSection?.querySelectorAll(this.c.gallery)
      ? [...this.$productSection.querySelectorAll(this.c.gallery)]
      : [];
    this.$details = this.$productSection?.querySelectorAll(this.c.detail)
      ? [...this.$productSection.querySelectorAll(this.c.detail)]
      : [];
    this.$variantSelectedColorOption = this.$productSection
      ?.querySelector(this.c.selectedColorOption);
    this.$variantSelectedSizeOptions = this.$productSection
      ?.querySelectorAll(this.c.selectedSizeOption);
    this.$variantColorAttributes = this.$productSection
      ?.querySelector(this.c.variantColorAttributes);
    this.$variantSizeAttributes = this.$productSection?.querySelector(this.c.variantSizeAttributes);
    this.$checkedColorOption = null;
    this.$checkedSizeOption = null;
    this.$checkedSizeOptions = null;

    // Data
    this.color = null;
    this.compare_at_price = null;
    this.finalDisplaySize = null;
    this.id = null;
    this.price = null;
    this.product = null;
    this.size = null;
    this.sizes = null;
    this.variant = null;
    this.variantAttributes = null;
  }

  /**
   * Init
   */
  async init() {
    if (!this.$productForm) return;

    // get Shopify product data
    this.product = await ProductConfigurables.getProductJson(
      this.$productForm?.dataset.productHandle,
    );
    SDG.Data.productJson = this.product;

    this.product.variants.forEach((variant, ind) => {
      variant.metafields = {
        ...SDG.Data.additionalProductData.variants[ind].metafields,
      };
    });

    // initialize product form
    this.productForm = new ProductForm(this.$productForm, this.product, {
      onOptionChange: this.onFormOptionChange.bind(this),
      onFormSubmit: (e) => e.preventDefault(),
    });

    // run update on page load to detect variant selected in URL
    this.onFormOptionChange();

    // update link for Get This Look product
    this.updateGTLLink();
  }

  /**
   * Get product JSON
   * @param {String} handle
   */
  static getProductJson(handle) {
    return fetch(`/products/${handle}.js`).then((response) => response.json());
  }

  /**
   * On form option change
   * @param {Object} event
   */
  onFormOptionChange() {
    // Set checked options
    this.$checkedColorOption = this.$productSection.querySelector(`${this.c.colorInput}:checked`);
    this.color = this.$checkedColorOption?.dataset.optionName;

    if (this.$variantSelectedSizeOptions.length > 1) {
      this.$checkedSizeOptions = Array.from(this.$productSection.querySelectorAll(`${this.c.sizeInput}:checked`));
      this.sizes = this.$checkedSizeOptions.map(($sizeOption) => $sizeOption.dataset.optionName);
    } else {
      this.$checkedSizeOption = this.$productSection.querySelector(`${this.c.sizeInput}:checked`);
      this.size = this.$checkedSizeOption?.dataset.optionName;
    }

    let variantOption;
    if (this.sizes && this.sizes.length > 0) {
      variantOption = `${this.sizes[0]} / ${this.sizes[1]}`;
    } else {
      variantOption = this.color && this.size
        ? `${this.color} / ${this.size}`
        : this.color || this.size;
    }

    const $variantTitles = Array.from(this.$hiddenId.querySelectorAll('option'));
    const $variantTitle = $variantTitles?.filter(($title) => (
      $title.innerText.trim() === variantOption
    ));

    // Set checked options data values
    if (this.$checkedSizeOptions) {
      this.$checkedSizeOptions.forEach(($checkedSizeOption) => {
        this.available = $checkedSizeOption
          ? $checkedSizeOption.dataset.available
          : null;
        this.compare_at_price = $checkedSizeOption
          ? $checkedSizeOption.dataset.variantPrice
          : null;
        this.price = $checkedSizeOption
          ? $checkedSizeOption.dataset.variantPrice
          : null;
      });
    } else {
      if (!this?.$checkedSizeOption && !this?.$checkedColorOption) {
        return;
      }

      this.available = this.$checkedSizeOption
        ? this.$checkedSizeOption.dataset.available
        : this.$checkedColorOption.dataset.available;
      this.compare_at_price = this.$checkedSizeOption
        ? this.$checkedSizeOption.dataset.variantPrice
        : this.$checkedColorOption.dataset.variantPrice;
      this.price = this.$checkedSizeOption
        ? this.$checkedSizeOption.dataset.variantPrice
        : this.$checkedColorOption.dataset.variantPrice;
    }

    this.id = $variantTitle.length > 0
      ? $variantTitle[0].value
      : null;
    this.variantAttributes = $variantTitle.length > 0
      ? $variantTitle[0].dataset.variantAttributes
      : null;
    this.finalDisplaySize = $variantTitle.length > 0
      ? $variantTitle[0].dataset.finalDisplaySize
      : null;

    const variant = this.product.variants.find((item) => (
      item.id === __.toNumber(this.id)
    ));

    // Set variant
    this.variant = {
      ...variant,
      price: `$${variant.price / 100}`,
      compare_at_price: `$${variant.compare_at_price / 100}`,
    };

    // Update UI
    this.updateDiscontinuedVariants();
    this.updateActiveState();
    this.updatePrice();
    this.updateComparePrice();
    this.updateVariantColorDetails();
    this.updateVariantSizeDetails();
    this.updateVariantSizeOptionPrices();
    this.updateVariantSelectedColorOptions();
    this.updateVariantSelectedSizeOptions();
    this.updateSubmitButton();
    this.updateSubmitLink();
    this.updateNotifyForm();
    this.updateGallery();
    this.updateProductDetails();
    this.updateHiddenId();
    this.updateBrowserHistory();
    this.updateHiddenFSNumber();
    this.changeDeliveryEstimate();
  }

  /**
   * Update active state
   */
  updateActiveState() {
    if (!this.variant.id) return;

    const $optionsFiltered = [...this.$productSection.querySelectorAll(`${this.c.optionGroup} input:not(:checked)`)];
    const $selectedOptions = [...this.$productSection.querySelectorAll(`${this.c.optionGroup} input:checked`)];

    if (!$optionsFiltered.length) return;

    // Remove all active classes
    $optionsFiltered.forEach(this.deactivateOption.bind(this));

    // Add active class to selected options
    $selectedOptions.forEach(this.activateOption.bind(this));
  }

  /**
   * Deactivate option
   * @param {Element} $input
   */
  deactivateOption($input) {
    const $option = $input.closest('.js-option');

    // Remove active class from option, remove 'checked' from input
    __.removeClass($option, this.c.active);
    $option.setAttribute('aria-checked', 'false');
    $input.removeAttribute('checked');
  }

  /**
   * Activate option
   * @param {Element} $option
   */
  activateOption($input) {
    const $option = $input.closest('.js-option');

    // Add active class to option, check the input
    __.addClass($option, this.c.active);
    $option.setAttribute('aria-checked', 'true');
    $input.setAttribute('checked', '');
  }

  /**
   * Update price
   */
  updatePrice() {
    if (!this.variant.id || this.$prices.length === 0) return;

    this.$prices.forEach(($price) => {
      $price.innerText = this.variant.price;
    });
  }

  /**
   * Update price compare
   */
  updateComparePrice() {
    if (!this.variant.id || !this.$priceCompare) return;

    if (this.variant.compare_at_price > this.variant.price) {
      this.$priceCompare.innerText = this.variant.compare_at_price;
      __.show(this.$priceCompare);
    } else {
      this.$priceCompare.innerText = '';
      __.hide(this.$priceCompare);
    }
  }

  /**
   * Update variant color details
   */
  updateVariantColorDetails() {
    if (!this.variant.id || !this.$variantColorAttributes) return;

    if (!this.variantAttributes) {
      this.$variantColorAttributes.innerText = '';
    } else {
      this.$variantColorAttributes.innerText = `${this.variantAttributes}`;
    }
  }

  /**
   * Update variant size details
   */
  updateVariantSizeDetails() {
    if (!this.variant.id || !this.$variantSizeAttributes) return;

    if (!this.finalDisplaySize) {
      this.$variantSizeAttributes.innerText = '';
    } else {
      this.$variantSizeAttributes.innerText = `Final display size: ${this.finalDisplaySize}`;
    }
  }

  updateVariantSizeOptionPrices() {
    const $variantSizeBtnPrices = this.$productSection.querySelectorAll(this.c.btnPrice);
    if (!this.variant.id || !$variantSizeBtnPrices.length) return;

    const selectedOptions = this.productForm.options();

    $variantSizeBtnPrices.forEach(($variantSizeBtnPrice) => {
      const { optionName, optionValue } = $variantSizeBtnPrice.dataset;
      const selectedOptionsMap = selectedOptions
        .map((option) => {
          if (option.name === optionName) return null;

          return option.value;
        })
        .filter((value) => value);

      const variant = this.product.variants.find((item) => {
        const variantOptions = item.options.map((option) => __.handleize(option).replaceAll('-', '_'));

        const isMatch = [...selectedOptionsMap, optionValue]
          .every((option) => variantOptions.includes(option));
        return isMatch;
      });

      if (!variant) {
        $variantSizeBtnPrice.innerText = '';
        return;
      }

      $variantSizeBtnPrice.innerText = `$${variant.price / 100}`;
    });
  }

  /**
   * Update variant selected color options
   */
  updateVariantSelectedColorOptions() {
    if (!this.variant.id || !this.$variantSelectedColorOption) return;

    this.$variantSelectedColorOption.innerText = `${this.color}`;
  }

  /**
   * Update variant selected size options
   */
  updateVariantSelectedSizeOptions() {
    if (!this.variant.id || !this.$variantSelectedSizeOptions) return;

    this.$variantSelectedSizeOptions.forEach(($variantSizeOption, index) => {
      const variant = this.product.variants.find((item) => (
        item.id === __.toNumber(this.variant.id)
      ));

      if (this.variant.metafields.custom.discontinued) {
        $variantSizeOption.innerText = '';
      } else {
        $variantSizeOption.innerText = this.size
          ? `${this.size}`
          : variant.options[index];
      }
    });
  }

  /**
   * Update submit button
   */
  updateSubmitButton() {
    if (!this.$submitButton) return;

    if (!this.variant.id) {
      this.$submitButton.disabled = true;
      this.$submitButtonText.innerText = 'Unavailable';
    // eslint-disable-next-line max-len
    } else if (this.variant?.metafields?.custom.discontinued) {
      this.$submitButton.disabled = true;
      this.$submitButtonText.innerText = 'Unavailable';
    } else if (this.variant.available) {
      this.$submitButton.setAttribute('data-variant-id', this.variant.id);
      this.$submitButton.disabled = false;
      this.$submitButtonText.innerText = `Add to Cart - ${this.variant.price}`;
    } else {
      this.$submitButton.setAttribute('data-variant-id', this.variant.id);
      this.$submitButton.disabled = true;
      this.$submitButtonText.innerText = 'Out of Stock';
    }
  }

  /**
   * Update submit link
   */
  updateSubmitLink() {
    if (!this.$submitLink) return;

    // parse submit link href URL for search params
    const { href: url } = this.$submitLink;
    const urlParams = new URLSearchParams(url);
    const variantId = urlParams.get('variant');

    // replace variant ID for search param value
    const urlSplit = url.split(variantId);
    const newUrl = urlSplit.join(this.variant.id);

    // replace submit link href value
    this.$submitLink.href = newUrl;

    if (this.variant.available && !this.variant.metafields.custom.discontinued) {
      this.$submitLink.innerText = 'Upload Photos';
      __.removeClass(this.$submitLink, 'disabled');
      this.$submitLink.style.pointerEvents = 'auto';
    } else {
      this.$submitLink.innerText = 'Out of Stock';
      __.addClass(this.$submitLink, 'disabled');
      this.$submitLink.style.pointerEvents = 'none';
    }
  }

  /**
   * Update notify form
   */
  updateNotifyForm() {
    if (!this.$notify) return;

    if (!this.variant) {
      __.hide(this.$notify);
      return;
    }

    if (this.variant.available) {
      __.hide(this.$notify);
    } else {
      __.show(this.$notify);
    }

    this.$notifyForm.dataset.productId = this.product.id;
    this.$notifyForm.dataset.variantId = this.variant.id;
  }

  /**
   * Update gallery
   */
  updateGallery(arrangeMedia = false) {
    if (!this.variant || !this.$galleries || !this.$galleries.length) return;

    const $selectedGallery = this.$productSection.querySelector(`${this.c.gallery}[data-variant-id="${this.variant.id}"]`);

    if ($selectedGallery) {
      this.$galleries.forEach(($gallery) => {
        __.addClass($gallery, this.c.hide);
      });

      const $uploadedCell = $selectedGallery.querySelector('.js-variant-spec-cell') || null;
      const { arrangeImages, makeCarousel } = productGalleryCarousel() || null;
      const $carousel = $selectedGallery.querySelector(this.c.galleryCarousel) || null;
      if ($uploadedCell) {
        __.removeClass($uploadedCell, this.c.hide);

        const { variantId } = $selectedGallery.dataset;
        $selectedGallery.id = `gallery-${variantId}`;
        $uploadedCell.id = `postArtwork-${variantId}`;

        postArtwork({
          dom: {
            parent: `gallery-${variantId}`,
            cell: 'js-variant-spec-cell',
          },
        });
      } else {
        postArtwork();
      }

      if ($carousel) {
        if (arrangeImages && makeCarousel) {
          // small timeout for carousel images
          setTimeout(() => {
            if (arrangeMedia) {
              arrangeImages($carousel);
            } else {
              makeCarousel($carousel, true);
            }
          }, 200);
        }
      }

      __.removeClass($selectedGallery, this.c.hide);
    } else {
      // load variant gallery html
      fetch(`${window.Shopify.routes.root}variants/${this.variant.id}?section_id=product-gallery-variant`)
        .then((response) => response.text())
        .then((text) => {
          const container = document.getElementById(this.c.galleryParent);
          const galleryHTML = new DOMParser()
            .parseFromString(text, 'text/html')
            .querySelector(`${this.c.galleryWrap}[data-variant-id="${this.variant.id}"]`);

          if (galleryHTML && container) {
            container.appendChild(galleryHTML);
            this.$galleries = [...this.$productSection.querySelectorAll(this.c.gallery)];
            productGalleryZoomModal();
            this.updateGallery(true);
          }
        })
        .catch((e) => {
          console.error(e);
        });
    }
  }

  /**
   * Update product details
   */
  updateProductDetails() {
    if (!this.variant || !this.$details || !this.$details.length) return;

    this.$details.forEach(($detail) => {
      __.addClass($detail, this.c.hide);
    });

    const $selectedDetail = this.$productSection.querySelector(`${this.c.detail}[data-variant-id="${this.variant.id}"]`);
    if ($selectedDetail) {
      __.removeClass($selectedDetail, this.c.hide);
      if (!__.hasClass($selectedDetail, this.c.active)) {
        __.addClass($selectedDetail, this.c.active);
        // activate first accordion item
        const $firstAccBtn = $selectedDetail.querySelector(`${this.c.accBtn}:first-child`);
        if ($firstAccBtn) {
          $firstAccBtn.click();
        }
      }
    }
  }

  updateDiscontinuedVariants() {
    if (!this.variant) return;

    const $optionGroups = this.$productSection.querySelectorAll(this.c.optionGroup);
    if (!$optionGroups.length) return;
    $optionGroups.forEach(($optionGroup) => {
      const optionName = $optionGroup.dataset.optionName.toLowerCase();
      if (optionName === 'color' || optionName === 'frame style') return;
      const $options = $optionGroup.querySelectorAll(this.c.option);
      $options.forEach(($option) => {
        $option.classList.remove('hide');
      });
    });

    $optionGroups.forEach(($optionGroup) => {
      const optionName = $optionGroup.dataset.optionName.toLowerCase();
      const optionPosition = +$optionGroup.dataset.optionPosition;
      if (optionName === 'color' || optionName === 'frame style') return;

      const $options = $optionGroup.querySelectorAll(this.c.option);
      $options.forEach(($option) => {
        const { optionValue } = $option.dataset;
        this.product.variants.forEach((variant) => {
          switch (optionPosition) {
            case 1:
              if (variant.option1 === optionValue) {
                if (variant.option2 === this.variant.option2
                  && variant.option3 === this.variant.option3) {
                  if (variant.metafields.custom.discontinued) {
                    $option.classList.add('hide');
                  }
                }
              }
              break;
            case 2:
              if (variant.option2 === optionValue) {
                if (variant.option1 === this.variant.option1
                  && variant.option3 === this.variant.option3) {
                  if (variant.metafields.custom.discontinued) {
                    $option.classList.add('hide');
                  }
                }
              }
              break;
            case 3:
              if (variant.option3 === optionValue) {
                if (variant.option1 === this.variant.option1
                  && variant.option2 === this.variant.option2) {
                  if (variant.metafields.custom.discontinued) {
                    $option.classList.add('hide');
                  }
                }
              }
              break;
            default:
              break;
          }
        });
      });
    });
  }

  /**
   * Update hidden ID
   */
  updateHiddenId() {
    if (!this.variant) return;

    this.$hiddenId.value = this.variant.id;
  }

  /**
   * Update hidden FS Number
   */
  updateHiddenFSNumber() {
    const selectedOptionIndex = this.$hiddenId?.selectedIndex;
    const selectedOption = this.$hiddenId?.options?.[selectedOptionIndex];
    const fsNumber = selectedOption?.dataset.frameSpecNumber;

    if (fsNumber && this.$fsHiddenInput) {
      this.$fsHiddenInput.value = fsNumber;
    }
  }

  /**
   * Update browser history
   */
  updateBrowserHistory() {
    const { enableHistoryState } = this.productForm.element.dataset;

    if (!this.variant || enableHistoryState !== 'true') return;

    const url = getUrlWithVariant(window.location.href, this.variant.id);
    window.history.replaceState({ path: url }, '', url);
  }

  /**
   * Handle delivery estimate metafields
   */
  changeDeliveryEstimate() {
    const $swatch = this.$productSection.querySelector(`${this.c.option}.${this.c.active}`);
    if (!$swatch) return;

    const { hideDeliveryEstimate } = $swatch.dataset;

    const $getItBySection = document.querySelector(`${this.c.deliveryEstimate}`);
    const $deliveryEstimateOverwriteContainer = document.querySelector(`${this.c.deliveryEstimateOverwriteContainer}`);
    const $overwriteMessage = $swatch.querySelector(`${this.c.overwriteMessage}`);

    if (hideDeliveryEstimate === 'true') {
      __.hide($getItBySection);

      if ($overwriteMessage) {
        $deliveryEstimateOverwriteContainer.innerHTML = $overwriteMessage.innerHTML;
      }
    } else {
      __.show($getItBySection);
      $deliveryEstimateOverwriteContainer.innerHTML = '';
    }
  }

  /**
   * Update GTL link
   * @param {Object} event
   */
  updateGTLLink() {
    if (!this.$submitLink) return;
    const { ctaType } = this.$submitLink.dataset;

    if (ctaType === 'get this look') {
      const { gtlUrlMetafield, gtlParams } = this.$submitLink.dataset;
      let newUrl = '';

      // parse submit link href URL for search params
      if (gtlUrlMetafield.includes('?')) {
        newUrl = `${gtlUrlMetafield}&${gtlParams}`;
      } else {
        newUrl = `${gtlUrlMetafield}?${gtlParams}`;
      }

      // replace submit link href value
      this.$submitLink.href = newUrl;
    }
  }
}

export default ProductConfigurables;
